import React, { useState, useCallback, useMemo } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import useSWR from 'swr'
import { api, updateMatch, createMatch, fetchMatchVodCount, linkShoulderVods, VodTypeInfo } from '../services/api'
import Select from 'react-select'
import { Badge, Button, Panel, CompetitorSelect, SeasonSelect, VenueSelect, OverridableField, ImageManagement, PublishingWorkflow, SlugManager, teamTypeFromCompetition } from '../components'
import { usePublishingPolling } from '../hooks'
import { Match, PubContent } from '../types/match'
import { Competitor, Season, Venue } from '../types/forms'
import { formatToPacificTime, formatToVenueTime, convertUTCToPTInput, convertPTInputToUTC, getTimezoneAbbr } from '../utils/timezone'
import pageStyles from '../styles/page.module.scss'
import selectStyles from '../styles/react-select.module.scss'
import styles from './MatchDetail.module.scss'

// General Status options (11 states - simpler for operational overrides)
const STATUS_OPTIONS = [
  { value: '', label: 'Select status...' },
  { value: 'not_started', label: 'Not Started' },
  { value: 'started', label: 'Started' },
  { value: 'postponed', label: 'Postponed' },
  { value: 'suspended', label: 'Suspended' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'delayed', label: 'Delayed' },
  { value: 'live', label: 'Live' },
  { value: 'interrupted', label: 'Interrupted' },
  { value: 'ended', label: 'Ended' },
  { value: 'closed', label: 'Closed' },
  { value: 'abandoned', label: 'Abandoned' },
] as const

const ROUND_OPTIONS = [
  { value: 'R1', label: 'R1' },
  { value: 'R2', label: 'R2' },
  { value: 'R3', label: 'R3' },
  { value: 'R4', label: 'R4' },
  { value: 'R5', label: 'R5' },
  { value: 'Quarterfinal', label: 'Quarterfinal' },
  { value: 'Semifinal', label: 'Semifinal' },
  { value: 'Final', label: 'Final' },
  { value: 'Q1', label: 'Q1' },
  { value: 'Q2', label: 'Q2' },
  { value: 'Q3', label: 'Q3' },
  { value: 'Qualifying Final', label: 'Qualifying Final' },
]

// VOD Publishing Status helpers
const getVodPublishStatusVariant = (status: string | undefined): 'success' | 'warning' | 'error' | 'neutral' => {
  switch (status) {
    case 'PUBLISHED_CURRENT':
    case 'PUBLISHED_OUT_OF_DATE': // Same as PUBLISHED_CURRENT because we don't require a publish after vod asset ingestion
      return 'success'
    case 'VIRTUAL_ONLY':
    case 'NO_ACTIVE_PUB_CONTENT':
    case 'NO_PUBLISH_MESSAGE':
      return 'warning'
    case 'UNLINKED_NO_MATCH':
      return 'error'
    default:
      return 'neutral'
  }
}

const formatVodPublishStatus = (status: string | undefined): string => {
  if (!status) return '—'
  switch (status) {
    case 'PUBLISHED_CURRENT':
    case 'PUBLISHED_OUT_OF_DATE': // Same as PUBLISHED_CURRENT because we don't require a publish after vod asset ingestion
      return 'Current'
    case 'VIRTUAL_ONLY':
      return 'Virtual'
    case 'NO_ACTIVE_PUB_CONTENT':
    case 'NO_PUBLISH_MESSAGE':
      return 'Not Published'
    case 'UNLINKED_NO_MATCH':
      return 'Unlinked'
    default:
      return status
  }
}

interface FormData {
  start_time: string
  end_time: string
  resume_time: string
  status: string
  match_status: string
  round: string
  home_competitor: Competitor | null
  away_competitor: Competitor | null
  winner_competitor: Competitor | null
  season: Season | null
  venue: Venue | null
}

const MatchDetail: React.FC = () => {
  const { matchId } = useParams<{ matchId: string }>()
  const navigate = useNavigate()
  const [isSaving, setIsSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [isCreatingSegment, setIsCreatingSegment] = useState(false)

  // Detect create mode
  const isCreateMode = matchId === 'new'

  const { data, isLoading, error, mutate } = useSWR(
    // Don't fetch if we're in create mode
    matchId && !isCreateMode ? ['match', matchId] : null,
    () => api.get(`/matches/${matchId}`),
    { revalidateOnFocus: false }
  )

  const match: Match | null = data ? (data?.body ? JSON.parse(data.body) : data) : null

  // pub_content is an array of publishable content items
  const pubContent = useMemo(() => match?.pub_content || [], [match?.pub_content])

  // Check if the most recent segment is cancelled (to show "Create New Segment" button)
  // pubContent is ordered by segment_number asc, so last item is most recent
  const mostRecentSegment = useMemo(() => {
    if (pubContent.length === 0) return null
    return pubContent[pubContent.length - 1]
  }, [pubContent])

  const mostRecentSegmentCancelled = useMemo(() => {
    return mostRecentSegment?.status === 'CANCELLED'
  }, [mostRecentSegment])

  // Enable lightweight polling when any item is in PUBLISHING state
  const hasPublishingItem = pubContent.some((item: PubContent) => item.status === 'PUBLISHING')
  usePublishingPolling(
    hasPublishingItem ? 'PUBLISHING' : undefined,
    mutate,
    2000 // Poll every 2 seconds
  )

  // Fetch VOD count for this match
  const { data: vodCountData, isLoading: isVodCountInitialLoading, isValidating: isVodCountValidating, mutate: mutateVodCount } = useSWR(
    matchId && !isCreateMode ? ['match-vod-count', matchId] : null,
    () => fetchMatchVodCount(matchId!),
    { revalidateOnFocus: false }
  )
  const vodCount = vodCountData?.count ?? 0
  const vodHighlight: VodTypeInfo | undefined = vodCountData?.vods?.highlight
  const vodCondensed: VodTypeInfo | undefined = vodCountData?.vods?.condensed
  // Use isValidating for refresh feedback (isLoading is only for initial load)
  const isVodCountLoading = isVodCountInitialLoading || isVodCountValidating

  // Link shoulder VODs state
  const [isLinkingVods, setIsLinkingVods] = useState(false)
  const [linkVodsResult, setLinkVodsResult] = useState<string | null>(null)
  const [linkVodsError, setLinkVodsError] = useState<string | null>(null)

  const handleLinkShoulderVods = async () => {
    if (!matchId) return
    setIsLinkingVods(true)
    setLinkVodsResult(null)
    setLinkVodsError(null)
    try {
      const result = await linkShoulderVods(matchId)
      const created = result.created_virtual_containers.length
      const linked = result.linked_wsc_assets.length
      const updated = result.updated_wsc_assets.length
      if (created === 0 && linked === 0 && updated === 0) {
        setLinkVodsResult('No changes needed')
      } else {
        const parts = []
        if (created > 0) parts.push(`${created} virtual container(s) created`)
        if (linked > 0) parts.push(`${linked} WSC asset(s) linked`)
        if (updated > 0) parts.push(`${updated} asset(s) metadata updated`)
        setLinkVodsResult(parts.join(', '))
      }
      mutateVodCount()
    } catch (err: any) {
      setLinkVodsError(err.message || 'Failed to link shoulder VODs')
    } finally {
      setIsLinkingVods(false)
    }
  }

  // No conversion helpers needed - backend returns unified shape! 🎉

  // Sparse object tracking only user changes (not saved yet)
  const [dirtyFields, setDirtyFields] = useState<Partial<FormData>>({})

  // Compute effective values: server data + local changes overlay
  const effectiveValues = useMemo((): FormData => {
    if (!match) {
      // In create mode, use dirtyFields directly since there's no server data
      return {
        start_time: dirtyFields.start_time ?? '',
        end_time: dirtyFields.end_time ?? '',
        resume_time: dirtyFields.resume_time ?? '',
        status: dirtyFields.status ?? '',
        match_status: dirtyFields.match_status ?? '',
        round: dirtyFields.round ?? '',
        home_competitor: dirtyFields.home_competitor ?? null,
        away_competitor: dirtyFields.away_competitor ?? null,
        winner_competitor: dirtyFields.winner_competitor ?? null,
        season: dirtyFields.season ?? null,
        venue: dirtyFields.venue ?? null,
      }
    }

    const resolveServerValue = (field: keyof FormData, options?: { skipTc?: boolean }) => {
      const tc   = (match.match as any)[`tc_${field}`]
      const auto = (match.match as any)[`auto_${field}`]
      const ext  = (match.match as any)[`ext_${field === 'round' ? 'display_round' : field}`]

      const skipTc = options?.skipTc === true

      // If not skipping TC, prefer tc -> auto -> ext
      if (!skipTc) {
        return tc ?? auto ?? ext;
      } else {
        // If skipping TC (e.g. user reverted locally), prefer auto -> ext (no tc)
        return auto ?? ext;
      }
    }

    const getEffectiveValue = (field: keyof FormData): any => {
      // If user has made a change (in dirtyFields)
      if (field in dirtyFields) {
        const dirtyValue = dirtyFields[field]
        // null means "reverted to auto/external" - show auto/external value in UI
        if (dirtyValue === null) {
          return resolveServerValue(field, { skipTc: true })
        }
        // Otherwise show user's pending change
        return dirtyValue
      }

      // No user change - show server value (TC override || auto || external)
      return resolveServerValue(field) ||
        (field === 'start_time' || field === 'end_time' || field === 'resume_time' || field === 'status' || field === 'match_status' || field === 'round' ? '' : null)
    }

    return {
      start_time: getEffectiveValue('start_time'),
      end_time: getEffectiveValue('end_time'),
      resume_time: getEffectiveValue('resume_time'),
      status: getEffectiveValue('status'),
      match_status: getEffectiveValue('match_status'),
      round: getEffectiveValue('round'),
      home_competitor: getEffectiveValue('home_competitor'),
      away_competitor: getEffectiveValue('away_competitor'),
      winner_competitor: getEffectiveValue('winner_competitor'),
      season: getEffectiveValue('season'),
      venue: getEffectiveValue('venue'),
    }
  }, [match, dirtyFields])

  // Determine team type from the match's competition (singles, doubles, or mixed).
  // Competition type is authoritative — placeholder/virtual competitors can
  // have a mismatched tc_team_type, so we don't rely on the per-competitor
  // signal. In create mode we don't yet know the competition, so show both
  // players and teams via 'mixed'. Fall back to competitor team_type only if
  // competition type is missing.
  const teamType = useMemo<'singles' | 'doubles' | 'mixed'>(() => {
    if (isCreateMode) return 'mixed'
    if (!match) return 'singles'
    const fromCompetition = teamTypeFromCompetition(
      match?.competition?.tc_type ?? match?.competition?.ext_type
    )
    if (fromCompetition) return fromCompetition
    const homeType =
      match?.match?.tc_home_competitor?.team_type ??
      match?.match?.ext_home_competitor?.team_type
    const awayType =
      match?.match?.tc_away_competitor?.team_type ??
      match?.match?.ext_away_competitor?.team_type
    return homeType === 'doubles' && awayType === 'doubles' ? 'doubles' : 'singles'
  }, [isCreateMode, match])

  // Determine if match was ever actually suspended (historical fact)
  const wasSuspended = useMemo(() => {
    if (!match) return false
    // Currently suspended (saved state)
    if (match?.match?.tc_status === 'suspended' || match?.match?.ext_status === 'suspended') return true
    // Has a resume time set
    if (match?.match?.tc_resume_time || match?.match?.ext_resume_time) return true
    // Has multiple segments (canonical evidence of past suspension)
    if (pubContent.some((pc: PubContent) => pc.segment_number > 1)) return true
    return false
  }, [match, pubContent])

  // Determine if we should show the resume_time field
  // Show if match was suspended OR user is about to suspend it
  // But hide if the active segment is REPUBLISH (uses start time) unless a resume time is already set
  const showResumeTime = useMemo(() => {
    // User selected suspended in dropdown (even before saving)
    if (effectiveValues.status === 'suspended') return true
    // If active segment is REPUBLISH, only show if a resume time is already set in the data
    if (mostRecentSegment?.segment_reason === 'REPUBLISH') {
      return !!(match?.match?.tc_resume_time || match?.match?.ext_resume_time || match?.match?.auto_resume_time)
    }
    // Match was actually suspended at some point
    return wasSuspended
  }, [effectiveValues.status, wasSuspended, mostRecentSegment, match])

  // Determine which time field the most recent segment uses for publishing
  const activeTimeField = useMemo(() => {
    if (!mostRecentSegment || mostRecentSegment.segment_number <= 1) return null
    return mostRecentSegment.segment_reason === 'SUSPENSION' ? 'resume_time' : 'start_time'
  }, [mostRecentSegment])

  const handleInputChange = useCallback(
    (field: keyof FormData, value: string | Competitor | Season | Venue | null) => {
      setDirtyFields(prev => ({ ...prev, [field]: value }))
    },
    []
  )

  // Check if there are any unsaved changes
  const hasChanges = useMemo(() => {
    return Object.keys(dirtyFields).length > 0
  }, [dirtyFields])

  const handleSave = async () => {
    setIsSaving(true)
    setSaveError(null)

    try {
      if (isCreateMode) {
        // CREATE MODE: Build payload from effectiveValues
        const createPayload: Record<string, any> = {}

        // Required field
        if (effectiveValues.start_time) {
          createPayload.tc_start_time = effectiveValues.start_time
        } else {
          setSaveError('Start time is required')
          setIsSaving(false)
          return
        }

        // Optional fields
        if (effectiveValues.end_time) createPayload.tc_end_time = effectiveValues.end_time
        if (effectiveValues.status) createPayload.tc_status = effectiveValues.status
        if (effectiveValues.round) createPayload.tc_round = effectiveValues.round
        if (effectiveValues.season?.tc_id) createPayload.tc_season_id = effectiveValues.season.tc_id
        if (effectiveValues.venue?.tc_id) createPayload.tc_venue_id = effectiveValues.venue.tc_id
        if (effectiveValues.home_competitor?.tc_id) createPayload.tc_home_competitor_id = effectiveValues.home_competitor.tc_id
        if (effectiveValues.away_competitor?.tc_id) createPayload.tc_away_competitor_id = effectiveValues.away_competitor.tc_id
        if (effectiveValues.winner_competitor?.tc_id) createPayload.tc_winner_competitor_id = effectiveValues.winner_competitor.tc_id

        const result = await createMatch(createPayload)
        const newMatchId = result.tc_match_id

        // Navigate to the new match's detail page
        navigate(`/matches/${newMatchId}`, { replace: true })
        return
      }

      // UPDATE MODE: Existing logic
      if (!matchId || !match) return

      // If no fields have changed, don't make the API call
      if (Object.keys(dirtyFields).length === 0) {
        return
      }

      // Helper: Extract TC ID only if it differs from external (indicates override)
      const getOverrideId = (
        entity: { tc_id: string; ext_id: string } | null,
        externalEntity: { ext_id: string } | null
      ): string | null => {
        return entity && entity.ext_id !== externalEntity?.ext_id ? entity.tc_id : null
      }

      // Entity fields that need override ID extraction
      const entityFields = ['home_competitor', 'away_competitor', 'winner_competitor', 'season', 'venue']

      const apiPayload: Record<string, any> = {}
      Object.entries(dirtyFields).forEach(([key, value]) => {
        if (entityFields.includes(key)) {
          // Entity field - extract override ID and send with '_id' suffix and 'tc_' prefix
          // If value is null (reverted), send null to clear the override
          if (value === null) {
            apiPayload[`tc_${key}_id`] = null
          } else {
            const externalKey = `ext_${key}` as keyof typeof match.match
            apiPayload[`tc_${key}_id`] = getOverrideId(
              value as any,
              match.match[externalKey] as any
            )
          }
        } else {
          // Simple field - add 'tc_' prefix for API
          // If value is null (reverted), send null to clear the override
          apiPayload[`tc_${key}`] = value
        }
      })

      await updateMatch(matchId, apiPayload)
      await mutate()
      // Clear dirty fields after successful save
      setDirtyFields({})
    } catch (err) {
      setSaveError((err as Error).message || 'Failed to save match')
    } finally {
      setIsSaving(false)
    }
  }

  const handleCancel = useCallback(() => {
    setDirtyFields({})
    setSaveError(null)
  }, [])

  // Create a new segment from a cancelled one
  const handleCreateNewSegment = async (segmentReason: 'SUSPENSION' | 'REPUBLISH') => {
    if (!matchId) return
    setIsCreatingSegment(true)
    setSaveError(null)
    try {
      await api.post(`/matches/${matchId}/workflow/new-segment`, { segment_reason: segmentReason })
      await mutate()
    } catch (err: any) {
      setSaveError(err.message || 'Failed to create new segment')
    } finally {
      setIsCreatingSegment(false)
    }
  }

  // Revert a single field - removes it from dirtyFields or sets to null if TC override exists
  const handleRevert = useCallback((field: keyof FormData) => {
    if (!match) return

    // Check if there's a TC override in the server data
    const tcFieldKey = `tc_${field}` as keyof typeof match.match
    const hasTcOverride = match.match[tcFieldKey] !== null && match.match[tcFieldKey] !== undefined

    setDirtyFields(prev => {
      const newDirtyFields = { ...prev } as any

      if (hasTcOverride) {
        // If there's a TC override in the database, set to null to clear it on save
        newDirtyFields[field] = null
      } else {
        // If there's no TC override in the database, just remove from dirty fields
        // (user changed something but then reverted back to external value)
        delete newDirtyFields[field]
      }

      return newDirtyFields as Partial<FormData>
    })
  }, [match])

  // Get the effective venue (TC override or external)
  const effectiveVenue = useMemo(() => {
    if (!match) return null
    return effectiveValues.venue || match?.match?.ext_venue
  }, [match, effectiveValues.venue])

  if (error && !isCreateMode) {
    return (
      <div className={pageStyles.pageContainer}>
        <div className={pageStyles.error}>
          <h2>Error loading match</h2>
          <p>Failed to fetch match details: {(error as Error).message}</p>
          <Button variant="danger" onClick={() => mutate()}>Retry</Button>
        </div>
      </div>
    )
  }

  if (!isCreateMode && (isLoading || !match)) {
    return (
      <div className={pageStyles.pageContainer}>
        <div className={pageStyles.loading}>
          <h3>Loading match details...</h3>
          <p>Please wait while we fetch the match data.</p>
        </div>
      </div>
    )
  }

  return (
    <div>
      <div className={pageStyles.pageHeader}>
        <h1>{isCreateMode ? 'Create Match' : 'Match Details'}</h1>
        {!isCreateMode && match && (
          <p>
            <code className="id-code">{match?.match?.tc_match_id}</code>
            {match?.match?.ext_event_id && (
              <> <code className="id-code">{match?.match?.ext_event_id}</code></>
            )}
          </p>
        )}
      </div>

      {saveError && (
        <div className={pageStyles.error}>
          <p>Error: {saveError}</p>
        </div>
      )}

      <div className={styles.detailContent}>
        {/* Left Column: Match Details Form */}
        <div className={styles.leftColumn}>
          <Panel margin="none">
            <Panel.Header>
              <div className={styles.vodHeaderRow}>
                <h2>Match Details</h2>
                {match?.match?.ext_updated_at && (
                  <span className={styles.lastSynced}>
                    Synced {formatToPacificTime(match.match.ext_updated_at)}
                  </span>
                )}
              </div>
            </Panel.Header>
            <div className={styles.fieldGrid}>
              <OverridableField
                label="Tournament Season"
                tcValue={effectiveValues.season?.ext_id || null}
                extValue={match?.match?.ext_season?.ext_id || null}
                extDisplayValue={
                  match?.match?.ext_season?.name && match?.match?.ext_season?.year
                    ? `${match?.match?.ext_season.name} (${match?.match?.ext_season.year})`
                    : match?.match?.ext_season?.ext_id || 'Unknown'
                }
                onRevert={() => handleRevert('season')}
              >
                <SeasonSelect
                  value={effectiveValues.season}
                  onChange={(season) => handleInputChange('season', season as Season | null)}
                  placeholder="Select season..."
                  isClearable
                  isModified={effectiveValues.season?.ext_id !== match?.match?.ext_season?.ext_id}
                />
              </OverridableField>

              <OverridableField
                label="Court"
                tcValue={effectiveValues.venue?.ext_id || null}
                extValue={match?.match?.ext_venue?.ext_id || null}
                extDisplayValue={
                  match?.match?.ext_venue?.name
                    ? `${match?.match?.ext_venue.name}${match?.match?.ext_venue.city ? `, ${match?.match?.ext_venue.city}` : ''}`
                    : match?.match?.ext_venue?.ext_id || 'Unknown'
                }
                onRevert={() => handleRevert('venue')}
              >
                <VenueSelect
                  value={effectiveValues.venue}
                  onChange={(venue) => handleInputChange('venue', venue as Venue | null)}
                  complexId={effectiveValues.season?.complex?.tc_id}
                  placeholder="Select court..."
                  isClearable
                  isModified={effectiveValues.venue?.ext_id !== match?.match?.ext_venue?.ext_id}
                />
              </OverridableField>

              <OverridableField
                label="Round"
                tcValue={effectiveValues.round}
                extValue={match?.match?.ext_display_round ?? null}
                onRevert={() => handleRevert('round')}
                modifiedClassName={styles.modified}
                extraInfo={
                  ((!match?.match?.tc_round && !('round' in dirtyFields)) || dirtyFields.round === null) && match?.match?.ext_display_round !== match?.match?.ext_round && (
                    <p><strong>Auto-transformed from source:</strong> {match?.match?.ext_round}</p>
                  )
                }
              >
                <Select
                  value={ROUND_OPTIONS.find(opt => opt.value === (effectiveValues.round || match?.match?.ext_display_round)) || null}
                  onChange={(option) => handleInputChange('round', option?.value || '')}
                  options={ROUND_OPTIONS}
                  isClearable
                  placeholder="Select round..."
                  className={selectStyles.reactSelect}
                  classNamePrefix="react-select"
                />
              </OverridableField>

              <OverridableField
                label="Status"
                tcValue={effectiveValues.status}
                extValue={match?.match?.ext_status ?? null}
                onRevert={() => handleRevert('status')}
                modifiedClassName={styles.modified}
              >
                <Select
                  options={STATUS_OPTIONS}
                  value={STATUS_OPTIONS.find(opt => opt.value === effectiveValues.status) || null}
                  onChange={(selectedOption) => handleInputChange('status', selectedOption?.value || '')}
                  isClearable={false}
                  placeholder="Select status..."
                  className={selectStyles.reactSelect}
                  classNamePrefix="react-select"
                />
              </OverridableField>

              <OverridableField
                label="Home Competitor"
                tcValue={effectiveValues.home_competitor?.ext_id || null}
                extValue={match?.match?.ext_home_competitor?.ext_id || null}
                extDisplayValue={match?.match?.ext_home_competitor?.name || match?.match?.ext_home_competitor?.ext_id || 'Unknown'}
                onRevert={() => handleRevert('home_competitor')}
              >
                <CompetitorSelect
                  value={effectiveValues.home_competitor}
                  onChange={(competitor) => handleInputChange('home_competitor', competitor as Competitor | null)}
                  teamType={teamType}
                  placeholder="Select home competitor..."
                  isModified={effectiveValues.home_competitor?.ext_id !== match?.match?.ext_home_competitor?.ext_id}
                />
              </OverridableField>

              <OverridableField
                label="Away Competitor"
                tcValue={effectiveValues.away_competitor?.ext_id || null}
                extValue={match?.match?.ext_away_competitor?.ext_id || null}
                extDisplayValue={match?.match?.ext_away_competitor?.name || match?.match?.ext_away_competitor?.ext_id || 'Unknown'}
                onRevert={() => handleRevert('away_competitor')}
              >
                <CompetitorSelect
                  value={effectiveValues.away_competitor}
                  onChange={(competitor) => handleInputChange('away_competitor', competitor as Competitor | null)}
                  teamType={teamType}
                  placeholder="Select away competitor..."
                  isModified={effectiveValues.away_competitor?.ext_id !== match?.match?.ext_away_competitor?.ext_id}
                />
              </OverridableField>

              <OverridableField
                label="Winner"
                tcValue={effectiveValues.winner_competitor?.ext_id || null}
                extValue={match?.match?.ext_winner_competitor?.ext_id || null}
                extDisplayValue={match?.match?.ext_winner_competitor?.name || match?.match?.ext_winner_competitor?.ext_id || 'Unknown'}
                onRevert={() => handleRevert('winner_competitor')}
              >
                <CompetitorSelect
                  value={effectiveValues.winner_competitor}
                  onChange={(competitor) => handleInputChange('winner_competitor', competitor as Competitor | null)}
                  teamType={teamType}
                  placeholder="Select winner..."
                  isClearable
                  isModified={effectiveValues.winner_competitor?.ext_id !== match?.match?.ext_winner_competitor?.ext_id}
                />
              </OverridableField>

              <div className={activeTimeField === 'start_time' ? styles.publishingSource : undefined}>
                {activeTimeField === 'start_time' && <span className={styles.publishingSourceLabel}>Publishing Source</span>}
                <OverridableField
                  label="Start Time (Pacific Time)"
                  tcValue={(dirtyFields.start_time !== null ? (match?.match?.tc_start_time ?? null) : null)}
                  autoValue={match?.match?.auto_start_time ?? null}
                  autoDisplayValue={match?.match?.auto_start_time ? formatToPacificTime(match?.match?.auto_start_time) : null}
                  extValue={match?.match?.ext_start_time ?? null}
                  extDisplayValue={match?.match?.ext_start_time ? formatToPacificTime(match?.match?.ext_start_time) : null}
                  onRevert={() => handleRevert('start_time')}
                  modifiedClassName={styles.modified}
                  extraInfo={
                    effectiveValues.start_time && effectiveVenue?.timezone && (
                      <p><strong>Court Local:</strong> {formatToVenueTime(effectiveValues.start_time, effectiveVenue.timezone)} {getTimezoneAbbr(effectiveValues.start_time, effectiveVenue.timezone)}</p>
                    )
                  }
                >
                  <input
                    type="datetime-local"
                    value={convertUTCToPTInput(effectiveValues.start_time)}
                    onChange={(e) => {
                      // Convert PT datetime-local to UTC ISO for storage
                      const utcValue = e.target.value ? convertPTInputToUTC(e.target.value) : ''
                      handleInputChange('start_time', utcValue)
                    }}
                    className="input"
                  />
                </OverridableField>
              </div>

              {/* Resume Time - only shown for suspended/was-suspended matches */}
              {showResumeTime && (
                <div className={activeTimeField === 'resume_time' ? styles.publishingSource : undefined}>
                  {activeTimeField === 'resume_time' && <span className={styles.publishingSourceLabel}>Publishing Source</span>}
                  <OverridableField
                    label="Resume Time (Pacific Time)"
                    tcValue={(dirtyFields.resume_time !== null ? (match?.match?.tc_resume_time ?? null) : null)}
                    autoValue={match?.match?.auto_resume_time ?? null}
                    autoDisplayValue={match?.match?.auto_resume_time ? formatToPacificTime(match?.match?.auto_resume_time) : null}
                    extValue={match?.match?.ext_resume_time ?? null}
                    extDisplayValue={match?.match?.ext_resume_time ? formatToPacificTime(match?.match?.ext_resume_time) : null}
                    onRevert={() => handleRevert('resume_time')}
                    modifiedClassName={styles.modified}
                    extraInfo={
                      effectiveValues.resume_time && effectiveVenue?.timezone && (
                        <p><strong>Court Local:</strong> {formatToVenueTime(effectiveValues.resume_time, effectiveVenue.timezone)} {getTimezoneAbbr(effectiveValues.resume_time, effectiveVenue.timezone)}</p>
                      )
                    }
                  >
                    <input
                      type="datetime-local"
                      value={convertUTCToPTInput(effectiveValues.resume_time)}
                      onChange={(e) => {
                        const utcValue = e.target.value ? convertPTInputToUTC(e.target.value) : ''
                        handleInputChange('resume_time', utcValue)
                      }}
                      className="input"
                    />
                  </OverridableField>
                </div>
              )}

              <OverridableField
                label="End Time (Pacific Time)"
                tcValue={effectiveValues.end_time}
                extValue={match?.match?.ext_end_time ?? null}
                extDisplayValue={match?.match?.ext_end_time ? formatToPacificTime(match?.match?.ext_end_time) : null}
                onRevert={() => handleRevert('end_time')}
                modifiedClassName={styles.modified}
                extraInfo={
                  effectiveValues.end_time && effectiveVenue?.timezone && (
                    <p><strong>Court Local:</strong> {formatToVenueTime(effectiveValues.end_time, effectiveVenue.timezone)} {getTimezoneAbbr(effectiveValues.end_time, effectiveVenue.timezone)}</p>
                  )
                }
              >
                <input
                  type="datetime-local"
                  value={convertUTCToPTInput(effectiveValues.end_time)}
                  onChange={(e) => {
                    // Convert PT datetime-local to UTC ISO for storage
                    const utcValue = e.target.value ? convertPTInputToUTC(e.target.value) : ''
                    handleInputChange('end_time', utcValue)
                  }}
                  className="input"
                />
              </OverridableField>
            </div>

            {/* Action Buttons */}
            <div className={styles.actionButtons}>
              <Button
                onClick={handleSave}
                size="large"
                disabled={isSaving || (!isCreateMode && !hasChanges)}
              >
                {isSaving ? 'Saving...' : isCreateMode ? 'Create Match' : 'Save Changes'}
              </Button>
              {!isCreateMode && (
                <Button
                  variant="secondary"
                  size="large"
                  onClick={handleCancel}
                  disabled={isSaving || !hasChanges}
                >
                  Reset Changes
                </Button>
              )}
            </div>
          </Panel>

          {/* Related VODs Section - only shown for existing matches */}
          {!isCreateMode && match && (
            <Panel margin="none">
              <Panel.Header>
                <div className={styles.vodHeaderRow}>
                  <h2>Related VODs</h2>
                  <button
                    type="button"
                    className={`${styles.refreshButton} ${isVodCountLoading ? styles.refreshing : ''}`}
                    onClick={() => mutateVodCount()}
                    disabled={isVodCountLoading}
                    aria-label="Refresh VOD count"
                    title="Refresh VOD count"
                  >
                    <svg
                      className={`${styles.refreshIcon} ${isVodCountLoading ? styles.spinning : ''}`}
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M21 12a9 9 0 1 1-9-9c2.52 0 4.93 1 6.74 2.74L21 8" />
                      <path d="M21 3v5h-5" />
                    </svg>
                  </button>
                </div>
              </Panel.Header>
              <div className={styles.vodContent}>
                {isVodCountLoading ? (
                  <p className={styles.vodNoResults}>Loading...</p>
                ) : vodCount === 0 ? (
                  <p className={styles.vodNoResults}>No VODs available</p>
                ) : (
                  <>
                    {/* Condensed VOD Row */}
                    <div className={styles.vodTypeRow}>
                      <span className={styles.vodTypeLabel}>Condensed</span>
                      <div className={styles.vodTypeStatus}>
                        {vodCondensed ? (
                          <Badge variant={getVodPublishStatusVariant(vodCondensed.publish_status)}>
                            {formatVodPublishStatus(vodCondensed.publish_status)}
                          </Badge>
                        ) : (
                          <span className={styles.vodTypeEmpty}>—</span>
                        )}
                      </div>
                      <div className={styles.vodTypeAction}>
                        {vodCondensed && (
                          <Link to={`/vod-highlights/${vodCondensed.tc_vod_asset_id}`}>
                            <Button variant="secondary" size="small">
                              View Details
                            </Button>
                          </Link>
                        )}
                      </div>
                    </div>

                    {/* Highlight VOD Row */}
                    <div className={styles.vodTypeRow}>
                      <span className={styles.vodTypeLabel}>Highlight</span>
                      <div className={styles.vodTypeStatus}>
                        {vodHighlight ? (
                          <Badge variant={getVodPublishStatusVariant(vodHighlight.publish_status)}>
                            {formatVodPublishStatus(vodHighlight.publish_status)}
                          </Badge>
                        ) : (
                          <span className={styles.vodTypeEmpty}>—</span>
                        )}
                      </div>
                      <div className={styles.vodTypeAction}>
                        {vodHighlight && (
                          <Link to={`/vod-highlights/${vodHighlight.tc_vod_asset_id}`}>
                            <Button variant="secondary" size="small">
                              View Details
                            </Button>
                          </Link>
                        )}
                      </div>
                    </div>

                    {/* View All Button */}
                    <div className={styles.vodViewAllRow}>
                      <Link to={`/vod-highlights?matchId=${matchId}`}>
                        <Button variant="secondary" size="small">
                          View All ({vodCount})
                        </Button>
                      </Link>
                    </div>
                  </>
                )}

                {/* Link Shoulder VODs action */}
                <div className={styles.vodLinkRow}>
                  <Button
                    variant="secondary"
                    size="small"
                    onClick={handleLinkShoulderVods}
                    disabled={isLinkingVods}
                  >
                    {isLinkingVods ? 'Linking...' : 'Link WSC Assets'}
                  </Button>
                  {linkVodsResult && (
                    <span className={styles.vodLinkSuccess}>{linkVodsResult}</span>
                  )}
                  {linkVodsError && (
                    <span className={styles.vodLinkError}>{linkVodsError}</span>
                  )}
                </div>
              </div>
            </Panel>
          )}
        </div>

        {/* Right Column: Publishing & Images - only shown for existing matches */}
        {!isCreateMode && (
          <div className={styles.rightColumn}>
            {/* Publishing Sections - one for each pub_content item */}
            {/* Backend always returns at least one (virtual if no DB record exists) */}
            {pubContent.map((item: PubContent, index: number) => {
              // Collapse cancelled segments when there are multiple
              const shouldCollapse = pubContent.length > 1 && item.status === 'CANCELLED'
              return (
                <Panel key={item.tc_pub_content_id || `virtual-${index}`} margin="none">
                  <PublishingWorkflow
                    matchId={matchId!}
                    pubContent={item}
                    competition={match?.competition}
                    onUpdate={mutate}
                    collapsible={shouldCollapse}
                    defaultCollapsed={shouldCollapse}
                    venueTimezone={effectiveVenue?.timezone}
                  />
                </Panel>
              )
            })}

            {/* Show segment creation buttons when most recent segment is cancelled */}
            {mostRecentSegmentCancelled && (
              <div className={styles.newSegmentSection}>
                <div className={styles.newSegmentButtons}>
                  <Button
                    onClick={() => handleCreateNewSegment('REPUBLISH')}
                    disabled={isCreatingSegment}
                  >
                    {isCreatingSegment ? 'Creating...' : 'Create Replacement Segment'}
                  </Button>
                  <Button
                    onClick={() => handleCreateNewSegment('SUSPENSION')}
                    disabled={isCreatingSegment}
                    variant="secondary"
                  >
                    {isCreatingSegment ? 'Creating...' : 'Create Suspension Segment'}
                  </Button>
                </div>
                <p className={styles.newSegmentHint}>
                  Replacement uses start time. Suspension uses resume time.
                </p>
              </div>
            )}

            {/* Images Section */}
            {match && (
              <Panel margin="none">
                <Panel.Header>
                  <h2>Images</h2>
                </Panel.Header>
                <ImageManagement entityType="match" entityId={matchId!} />
              </Panel>
            )}

            {/* URL Slug */}
            {match && !isCreateMode && (
              <SlugManager entityType="match" entityId={matchId!} />
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default MatchDetail
